import { apiFetch } from '@/config/api.js'

/**
 * 检测当前环境是否支持推送通知
 */
export function isPushSupported() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return false
  }
  // 检查是否在安全上下文中（HTTPS 或 localhost）
  if (window.isSecureContext === false) {
    return false
  }
  return true
}

/**
 * 获取通知权限状态
 */
export function getPushPermissionState() {
  if (!('Notification' in window)) return 'denied'
  return Notification.permission
}

/**
 * 订阅推送通知
 * @returns {Promise<boolean>} 是否成功
 */
export async function subscribeToPush() {
  // 请求通知权限
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return false
  }

  // 获取 Service Worker 注册
  const registration = await navigator.serviceWorker.ready

  // 从后端获取 VAPID 公钥
  const res = await apiFetch('/api/push/vapid-key', {}, true)
  if (res.code !== 0 || !res.data || !res.data.publicKey) {
    throw new Error('获取推送公钥失败')
  }

  const applicationServerKey = urlBase64ToUint8Array(res.data.publicKey)

  // 订阅推送
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey
  })

  // 提取订阅信息
  const subJson = subscription.toJSON()
  const endpoint = subJson.endpoint
  const p256dh = subJson.keys.p256dh
  const auth = subJson.keys.auth

  // 发送到后端保存，并校验返回值
  const subRes = await apiFetch('/api/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({ endpoint, p256dh, auth })
  })

  if (subRes.code !== 0) {
    // 后端保存失败，撤销浏览器端订阅
    await subscription.unsubscribe()
    throw new Error(subRes.msg || '保存推送订阅失败')
  }

  return true
}

/**
 * 取消推送订阅
 * @returns {Promise<boolean>} 是否成功
 */
export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()

  if (subscription) {
    const endpoint = subscription.endpoint

    // 先通知后端删除，再取消浏览器端订阅
    try {
      await apiFetch('/api/push/unsubscribe', {
        method: 'POST',
        body: JSON.stringify({ endpoint })
      })
    } catch (e) {
      console.error('通知后端取消订阅失败:', e)
    }

    await subscription.unsubscribe()
  }

  return true
}

/**
 * 检查当前是否已订阅
 * @returns {Promise<boolean>}
 */
export async function isCurrentlySubscribed() {
  if (!isPushSupported()) return false

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    return subscription !== null
  } catch (e) {
    return false
  }
}

/**
 * Base64url 字符串转 Uint8Array
 * pushManager.subscribe() 的 applicationServerKey 需要此格式
 */
function urlBase64ToUint8Array(base64String) {
  var padding = '='.repeat((4 - base64String.length % 4) % 4)
  var base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  var rawData = window.atob(base64)
  var outputArray = new Uint8Array(rawData.length)

  for (var i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
